import { useMemo, useState } from "preact/hooks";
import { Flame, Settings as SettingsIcon } from "lucide-preact";
import { Button, Chip, Panel, Ring } from "../components";
import { Crest } from "../components/Crest";
import { PLAQUES } from "../content/plaques";
import { closestMilestone } from "../engine/plaques";
import type { SessionType } from "../engine/session";
import { buildSession } from "../engine/session";
import { labelForItem } from "../booth/useSession";
import { dayStr } from "../lib/date";
import { installPromptEvent, promptInstall } from "../lib/installPrompt";
import { navigate } from "../lib/router";
import { startSession } from "../state/session";
import { hasSeenTip, loadSessionProgress, markTipSeen, profile, rank, streakPreview, weeklyRing } from "../state/store";

const INSTALL_TIP = "install-prompt";

const SESSION_TYPES: { id: SessionType; label: string }[] = [
  { id: "daily", label: "Daily" },
  { id: "twisters", label: "Twisters" },
  { id: "fluency", label: "Fluency" },
  { id: "weak", label: "Weak Sounds" },
  { id: "free", label: "Free Play" },
];

export function Today() {
  const p = profile.value;
  const today = dayStr();
  const r = rank.value;
  const preview = streakPreview.value;
  const ring = weeklyRing.value;
  const ringClosedToday = p.rings[today]?.closed ?? false;

  const [sessionType, setSessionType] = useState<SessionType>("daily");
  const items = useMemo(() => buildSession(p, sessionType, today), [p, sessionType, today]);
  const reviewCount = items.filter((i) => i.kind !== "drill").length;
  const closest = closestMilestone(p);
  const closestPlaque = closest ? PLAQUES.find((pl) => pl.id === closest.id) : null;

  const resumable = useMemo(() => {
    const saved = loadSessionProgress();
    return saved && saved.day === today && saved.type === sessionType ? saved : null;
  }, [today, sessionType]);

  const showInstallBanner = p.sessions >= 3 && !!installPromptEvent.value && !hasSeenTip(INSTALL_TIP);

  return (
    <div class="screen screen-today">
      <header class="id-strip">
        <button class="id-strip-identity" type="button" onClick={() => navigate("progress")}>
          <Crest status={r} seed={p.seed} size={28} />
          <span class="id-strip-rank display">{r.name}</span>
        </button>
        <span class="id-strip-day mono">DAY {p.sessions}</span>
        <span class="id-strip-streak mono">
          <Flame size={14} strokeWidth={2} style={{ color: "var(--ember)" }} />
          {p.streak.current}
        </span>
        <button class="id-strip-gear" type="button" aria-label="Settings" onClick={() => navigate("settings")}>
          <SettingsIcon size={20} strokeWidth={1.5} />
        </button>
      </header>

      <div class="today-ring-row">
        <Ring segments={7} filled={ring.filled} current={ringClosedToday ? undefined : ring.filled} size={108} centerLabel={ringClosedToday ? "DONE" : "TODAY"} />
        <div class="today-ring-caption">
          <p class="label">This week</p>
          <p class="mono">
            {ring.filled} / {p.week.target} target
          </p>
        </div>
      </div>

      {preview.wouldBreak && preview.repairEligible && (
        <Panel class="banner banner-ember">
          Your {preview.lostStreak}-day streak ended yesterday. Finish today's session to restore it.
        </Panel>
      )}
      {preview.restDaysToConsume > 0 && !preview.wouldBreak && (
        <Panel class="banner banner-muted">A rest day covered yesterday. Streak intact — {p.streak.current} days.</Panel>
      )}

      <Panel raised class="start-panel">
        <p class="display start-panel-title">Today's Session</p>
        <p class="mono start-panel-meta">
          {resumable ? `RESUMING — ${resumable.itemIndex} OF ${items.length} DONE` : `${items.length} ITEMS · ${reviewCount} DUE FOR TUNE-UP`}
        </p>
        <Button size="lg" breathing={!ringClosedToday} onClick={() => startSession(sessionType)}>
          {resumable ? `Resume — ${resumable.itemIndex} of ${items.length}` : ringClosedToday ? "Train again" : "Start"}
        </Button>
      </Panel>

      <div class="session-chips">
        {SESSION_TYPES.map((s) => (
          <Chip key={s.id} active={sessionType === s.id} onClick={() => setSessionType(s.id)}>
            {s.label}
          </Chip>
        ))}
      </div>

      <Panel class="next-up">
        <p class="label">Next up</p>
        <ul>
          {items.slice(0, 3).map((item, i) => (
            <li key={i}>{labelForItem(item)}</li>
          ))}
        </ul>
      </Panel>

      {showInstallBanner && (
        <Panel class="banner install-banner">
          <span>Add Diction Dojo to your homescreen — one tap in, no browser chrome.</span>
          <div class="install-banner-actions">
            <Button size="sm" variant="ghost" onClick={() => markTipSeen(INSTALL_TIP)}>
              Not now
            </Button>
            <Button
              size="sm"
              onClick={() => {
                markTipSeen(INSTALL_TIP);
                void promptInstall();
              }}
            >
              Install
            </Button>
          </div>
        </Panel>
      )}

      {closestPlaque && closest && (
        <Panel class="closest-plaque">
          <p class="label">Closest plaque</p>
          <p class="display">{closestPlaque.name}</p>
          <p class="closest-plaque-how">{closestPlaque.how}</p>
          <div class="closest-plaque-bar">
            <div class="closest-plaque-fill" style={{ width: `${closest.fraction * 100}%` }} />
          </div>
          <p class="mono closest-plaque-count">
            {closest.current} / {closest.target}
          </p>
        </Panel>
      )}
    </div>
  );
}
